import React, { useState, useEffect } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  MousePointerClick,
  Compass,
  Activity,
  Rocket,
  RefreshCw,
  Building2
} from 'lucide-react';

// Utility to disable/enable navigation buttons in some steps
function createButtonControls(setIsBackDisabled, setIsNextDisabled) {
  return {
    lockButtons: () => {
      setIsBackDisabled(true);
      setIsNextDisabled(true);
    },
    releaseButtons: () => {
      setIsBackDisabled(false);
      setIsNextDisabled(false);
    },
  };
}

const MobileGuidedDemo = ({ topoInstance, onDemoStart }) => {
  // Core state management
  const [isDemoStarted, setIsDemoStarted] = useState(false);
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [currentSubStepIndex, setCurrentSubStepIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  
  // Button states
  const [isBackDisabled, setIsBackDisabled] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  
  // Feature flags for tracking topology changes
  const [podOneIsAdded, setPodOneIsAdded] = useState(false);
  const [podTwoIsAdded, setPodTwoIsAdded] = useState(false);
  const [wanIsAdded, setWanIsAdded] = useState(false);

  // Initialize button controls
  const { lockButtons, releaseButtons } = createButtonControls(
    setIsBackDisabled, 
    setIsNextDisabled
  );

  const scrollToSection = (id: string) => {
    const [hash, queryString] = id.split('?');

    // Scroll to the section
    if (hash === "#" || hash === "") {
      // Scroll to the top of the page
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    // Update the URL with the query string
    const newUrl = queryString ? `${hash}?${queryString}` : hash;
    window.history.pushState({}, '', newUrl);

    // Manually trigger the `ApplicationForm` to react to the URL change
    window.dispatchEvent(new Event('popstate'));
  };

  // Hide built-in control panel on component mount
  useEffect(() => {
    if (topoInstance?.topology?.controlPanel) {
      topoInstance.topology.controlPanel.hide();
    }
  }, [topoInstance]);

  // Helper methods from the desktop version (like delay, getRandomFloat, scenario actions) 
  // are inlined below. For brevity, we re-copy the essential parts.

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  const getRandomFloat = (min, max) => {
    const r = Math.random() * (max - min) + min;
    return Number(r.toFixed(2));
  };

  // -----------------------------------------------------------
  // EXACT SCENARIOS FROM DESKTOP, but skip "Share or Export Map"
  // -----------------------------------------------------------

  const scenarios = [
    {
      id: 'interact',
      label: 'Explore Network',
      subSteps: [
        {
          label: "Navigate & Zoom",
          detail: "Pan around by swiping, and zoom in/out with the pinch gestures.",
          action: () => {
            lockButtons();
            topoInstance.topology.setZoom(1, {animate: true});
            topoInstance.topology.resetPanOffset();
            topoInstance.topology.applyLayout();
            setTimeout(() => {
                topoInstance.topology.setZoom(0.7, {animate: true});
                setTimeout(() => {
                  topoInstance.topology.setZoom(1, {animate: true});
                  releaseButtons();
              }, 800);
            }, 1200);
          }
        },
        {
          label: "Drag & Drop Nodes",
          detail: "Grab nodes or groups and drop them anywhere. Nodes automatically maintain connection clarity during movement - watch links reflow in real-time as you reposition.",
          action: () => {
            lockButtons();
            const r1 = topoInstance.topology.getNode('router1');
            setTimeout(() => {
              topoInstance.topology.setNodePosition(r1.id, r1.coord.x - 20, r1.coord.y);
          setTimeout(() => {
              topoInstance.topology.setNodePosition(r1.id, r1.coord.x + 40, r1.coord.y);
              setTimeout(() => {
                topoInstance.topology.setNodePosition(r1.id, r1.coord.x - 20, r1.coord.y);
                releaseButtons();
              }, 500);
            }, 500);
          }, 500);
          }
        },
        {
          label: "Inspect Interfaces",
          detail: "Tap interface indicators to expand labels. No more guesswork in your topology.",
          action: () => {
            if (topoInstance) {
              const node = topoInstance.topology.getNode('router2');
              if (node && node.interfaces['Gi0/0']) {
                node.interfaces['Gi0/0'].expand();
              }
            }
          }
        },
      ]
    },
    {
      id: 'health',
      label: 'Monitor Health',
      subSteps: [
        {
          label: "Highlight Issues",
          detail: "Change node, interface, and link states with a few lines of code to reflect real-time errors or warnings. Immediately spot ‘red’ critical links and keep an eye on ‘yellow’ warnings.",
          action: () => {
            if (topoInstance) {
              topoInstance.topology.getNode('router2').setStatus('error');
              topoInstance.topology.getNode('router2').setInterfaceStatus('Gi0/1', 'error');
              topoInstance.topology.getNode('router3').setStatus('warning');
              topoInstance.topology.getNode('router3').setInterfaceStatus('Gi0/0', 'warning');
              topoInstance.topology.getNode('router2').interfaces['Gi0/0'].edge.setStatus('warning')
              topoInstance.topology.getNode('router2').interfaces['Gi0/1'].edge.setStatus('error');
            }
          }
        },
        {
          label: "Visualize Link Load",
          detail: "Display link load data on your network map—topoSphere supports this feature out-of-the-box!",
          action: async () => {
            if (topoInstance) {
              topoInstance.topology.getNode('router2').setStatus('');
              topoInstance.topology.getNode('router2').setInterfaceStatus('Gi0/1', '');
              topoInstance.topology.getNode('router3').setStatus('');
              topoInstance.topology.getNode('router3').setInterfaceStatus('Gi0/0', '');
              topoInstance.topology.getNode('router2').interfaces['Gi0/0'].edge.setStatus('')
              topoInstance.topology.getNode('router2').interfaces['Gi0/1'].edge.setStatus('');

              await delay(500);
              await topoInstance.topology.setZoom(0.7);
              await delay(500);
              await topoInstance.topology.applyLayout();
              await topoInstance.topology.setZoom(0.85);

              let r1r2_util1 = topoInstance.topology.getUtilizationIndicator('router1', 'Gi0/0');
              let r1r2_util2 = topoInstance.topology.getUtilizationIndicator('router2', 'Gi0/0');
              let r1r3_util1 = topoInstance.topology.getUtilizationIndicator('router1', 'Gi0/1');
              let r1r3_util2 = topoInstance.topology.getUtilizationIndicator('router3', 'Gi0/1');

              if (!r1r2_util1) {
                r1r2_util1 = topoInstance.topology.addUtilizationIndicator(
                  'router1',
                  'Gi0/0',
                  {value: 50, label: '0.51 Gb/s', expand: true}
                )
              }
              if (!r1r2_util2) {
                r1r2_util2 = topoInstance.topology.addUtilizationIndicator(
                  'router2',
                  'Gi0/0',
                  {value: 90, label: '0.93 Gb/s', expand: true}
                )
              }
              if (!r1r3_util1) {
                r1r3_util1 = topoInstance.topology.addUtilizationIndicator(
                  'router1',
                  'Gi0/1',
                  {value: 50, label: '0.42 Gb/s', expand: false}
                )
              }
              if (!r1r3_util2) {
                r1r3_util2 = topoInstance.topology.addUtilizationIndicator(
                  'router3',
                  'Gi0/1',
                  {value: 90, label: '0.46 Gb/s', expand: false}
                )
              }

              // Function to update both utilization indicators
              const updateUtilizations = () => {
                const value1 = getRandomFloat(0.5, 1);
                const value2 = getRandomFloat(0.6, 0.85);
                const value3 = getRandomFloat(0.5, 1);
                const value4 = getRandomFloat(0.6, 0.85);
                
                r1r2_util1.updateUtilization(value1 * 100, `${value1} Gbps`);
                r1r2_util2.updateUtilization(value2 * 100, `${value2} Gbps`);
                r1r3_util1.updateUtilization(value3 * 100, `${value3} Gbps`);
                r1r3_util2.updateUtilization(value4 * 100, `${value4} Gbps`);
              };

              // Start periodic updates every 2 seconds (2000 milliseconds)
              const updateInterval = setInterval(updateUtilizations, 2000);
            }
          }
        },
        {
          label: "Animate Traffic Flows",
          detail: "See live packet animations across your topology to understand traffic patterns at a glance. Perfect for troubleshooting, capacity planning, or simply wowing your users.",
          action: async () => {
            
            if (topoInstance) {
              topoInstance.topology.getUtilizationIndicator('router1', 'Gi0/0')?.collapse();
              topoInstance.topology.getUtilizationIndicator('router2', 'Gi0/0')?.collapse();

              if (podOneIsAdded && podTwoIsAdded) {
                topoInstance.topology.removePath('interPodPath');
                await topoInstance.topology.addPath({
                  id: 'interPodPath',
                  nodeIds: ['pod1_leaf2', 'pod1_spine1', 'superSpine1', 'pod2_spine2', 'pod2_leaf5'],
                  animated: true,
                  theme: {
                    flowAnimationIcon: 'network.packet',
                    strokeStyle: 'rgba(0, 0, 255, 0.7)',
                  }
                });
              } else {
                await topoInstance.topology.addPath({
                  id: 'path1',
                  nodeIds: ['router1', 'router2', 'router3'],
                  animated: true,
                  theme: {
                    flowAnimationIcon: 'network.packet',
                    strokeStyle: 'rgba(0, 0, 255, 0.7)',
                  }
                });
              }
            }
          }
        },
      ]
    },
    {
      id: 'update',
      label: 'Adapt & Scale',
      subSteps: [
        {
          label: "Update Topology Dynamically",
          detail: "Inject new nodes and links or remove them on-the-fly. topoSphere updates links and repositions nodes automatically, showcasing how easy it is to reflect network changes in real time.",
          action: async () => {
            lockButtons();

            topoInstance.topology.resetPanOffset();

            await delay(300);
            if (!wanIsAdded) {
              await topoInstance.topology.addNode({
                id: 'wan',
                iconName: 'network.cloud',
                label: 'WAN',
                layer: 0,
                coord: { x: 0, y: 100 }
              });
              await topoInstance.topology.addEdge({
                source: 'router1',
                target: 'wan',
                DELETEsourceInterface: 'Gi0/2',
                DELETEtargetInterface: 'Gi0/0',
                sourceInterface: { labelText: 'Gi0/2' },
                targetInterface: { labelText: 'Gi0/0' },
              });
              await topoInstance.topology.setLayout('layered');
              setWanIsAdded(true);
            }
            await delay(800);
            topoInstance.topology.getNode('router1').labelText = 'Router 1';
            topoInstance.topology.getNode('router2').labelText = 'Router 2';
            topoInstance.topology.getNode('router3').labelText = 'Router 3';
            await delay(1000);
            if (!podOneIsAdded) await addPODOne();
            await topoInstance.topology.setLayout('layered');
            await topoInstance.topology.zoomToFit();
            releaseButtons();
          }
        },
        {
          label: "Experiment with Layouts",
          detail: "Switch between layered, force-directed, or horizontal/vertical flows. Let topoSphere’s built-in algorithms handle the heavy lifting for a clear, visually appealing map.",
          action: async () => {
            lockButtons();
            topoInstance.topology.groups.forEach(group => {
              group.expand();
              topoInstance.topology.removeGroup(group.id);
            });
            topoInstance.topology.addGroup({
              id: 'routers',
              name: 'Routers',
              memberNodes: ['router1', 'router2', 'router3']
            });

            await topoInstance.topology.setLayout('forceDirected');
            await delay(1200);
            await topoInstance.topology.setLayout('layered', {orientation: 'horizontal'});
            await delay(700);
            await topoInstance.topology.setLayout('layered', {orientation: 'vertical', order: 'descending'});
            await delay(700);
            await topoInstance.topology.setLayout('layered');

            releaseButtons();
          }
        },
        {
          label: "Group & Collapse Devices",
          detail: "Cluster related devices into groups and collapse them to reduce clutter. The SDK reroutes edges automatically, so your map stays logical and legible.",
          action: async () => {
            lockButtons();

            await delay(500);

            // Rename POD 1 devices for consistency
            topoInstance.topology.nodes.forEach(node => {
              if (
                  node.id && 
                  typeof node.id === 'string' && 
                  node.id.toLowerCase().startsWith('pod1') &&
                  node.labelText && 
                  typeof node.labelText === 'string' && 
                  !node.labelText.startsWith('POD1 ')
              ) {
                  // Append 'POD1 ' to the beginning of the existing label
                  node.labelText = 'POD1 ' + node.labelText;
              }
            });
            
            //topoInstance.topology.setZoom(1);
            
            if (!podTwoIsAdded) {
              await addPODTwo();
            }
            await topoInstance.topology.setLayout('layered');

            topoInstance.topology.zoomToFit();
            topoInstance.topology.removePath('interPodPath');
            addInterPodPath();

            await delay(1000);

            addPodGroups();

            await delay(2000);

            
            topoInstance.topology.getNode('pod2_leaf5').setStatus('normal');
            topoInstance.topology.getGroup('pod1')?.collapse()

            await delay(2000);
            topoInstance.topology.getGroup('pod2')?.collapse();

            await delay(1000);
            await topoInstance.topology.setZoom(0.5);
            await topoInstance.topology.setLayout('layered');

            releaseButtons();
          }
        },
        {
          label: "Combine All Features",
          detail: "Now see how easy it is to orchestrate multiple features in tandem—group collapses, link statuses, real-time expansions—all with minimal code overhead.",
          action: async () => {
            lockButtons();
            await addPODOne();
            await addPODTwo();
            await addPodGroups();
            addInterPodPath();

            topoInstance.topology.getGroup('pod2')?.collapse();
            topoInstance.topology.getNode('router1').setStatus('error');

            setTimeout(() => {
              topoInstance.topology.getGroup('routers')?.collapse();
              topoInstance.topology.getNode('pod2_leaf5').setStatus('warning');
              setTimeout(() => {
                topoInstance.topology.getGroup('pod2')?.expand();

                let r1wan_util1 = topoInstance.topology.getUtilizationIndicator('router1', 'Gi0/2');
                let r1wan_util2 = topoInstance.topology.getUtilizationIndicator('wan', 'Gi0/0');

                if (!r1wan_util1) {
                  r1wan_util1 = topoInstance.topology.addUtilizationIndicator(
                    'router1',
                    'Gi0/2',
                    {value: 91, label: '0.91 Gb/s', expand: true}
                  )
                }
                if (!r1wan_util2) {
                  r1wan_util2 = topoInstance.topology.addUtilizationIndicator(
                    'wan',
                    'Gi0/0',
                    {value: 70, label: '0.70 Gb/s', expand: true}
                  )
                }

                const updateUtilizations = () => {
                  const value1 = getRandomFloat(0.7, 0.8);
                  const value2 = getRandomFloat(0.85, 0.98);
                  
                  r1wan_util1.updateUtilization(value1 * 100, `${value1} Gbps`);
                  r1wan_util2.updateUtilization(value2 * 100, `${value2} Gbps`);
                };
  
                // Start periodic updates every 2 seconds (2000 milliseconds)
                const updateInterval = setInterval(updateUtilizations, 2000);

                releaseButtons();
              }, 700);
            }, 1500);
          }
        },
      ]
    },
  ];

  // Helper functions from the “desktop” code to build PODs, groups, etc. We’ll inline them here:
  // (Exact copies from desktop, but with setPodOneIsAdded, setPodTwoIsAdded, etc.)

  const addPODOne = async () => {
    if (podOneIsAdded) return;
    setPodOneIsAdded(true);
    // ADD LEAF-SPINE topology
    // Border Leaf Switches (Layer 2)
    // These switches connect the external routers to the spine layer
    await topoInstance.topology.addNode({
      id: 'pod1_borderLeaf1',
      iconName: 'network.switch',
      label: 'Border Leaf 1',
      layer: 300,
      coord: { x: -137.66666666666669, y: -131.38255122273628 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_borderLeaf2',
      iconName: 'network.switch',
      label: 'Border Leaf 2',
      layer: 300,
      coord: { x: 137.66666666666669, y: -131.38255122273628 }
    });

    // Spine Switches (Layer 3)
    // Core backbone of the network, providing redundant paths
    await topoInstance.topology.addNode({
      id: 'pod1_spine1',
      iconName: 'network.switch',
      label: 'Spine 1',
      layer: 301,
      coord: { x: -137.66666666666669, y: 32.2058162590879 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_spine2',
      iconName: 'network.switch',
      label: 'Spine 2',
      layer: 301,
      coord: { x: 137.66666666666669, y: 32.2058162590879 }
    });

    // Leaf Switches (Layer 4)
    // Access layer switches that connect to end devices
    await topoInstance.topology.addNode({
      id: 'pod1_leaf1',
      iconName: 'network.switch',
      label: 'Leaf 1',
      layer: 302,
      coord: { x: -285, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf2',
      iconName: 'network.switch',
      label: 'Leaf 2',
      layer: 302,
      coord: { x: -171, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf3',
      iconName: 'network.switch',
      label: 'Leaf 3',
      layer: 302,
      coord: { x: -57, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf4',
      iconName: 'network.switch',
      label: 'Leaf 4',
      layer: 302,
      coord: { x: 57, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf5',
      iconName: 'network.switch',
      label: 'Leaf 5',
      layer: 302,
      coord: { x: 171, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf6',
      iconName: 'network.switch',
      label: 'Leaf 6',
      layer: 302,
      coord: { x: 285, y: 195.79418374091208 }
    });

    // Router to Border Leaf Connections
    // Connecting external routers to the fabric
    await topoInstance.topology.addEdge({
      source: 'router2',
      target: 'pod1_borderLeaf1',
      DELETEsourceInterface: 'Gi0/3',
      DELETEtargetInterface: 'Eth0',
      sourceInterface: { labelText: 'Gi0/3', visible: true },
      targetInterface: { labelText: 'Eth0', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'router2',
      target: 'pod1_borderLeaf2',
      DELETEsourceInterface: 'Gi0/4',
      DELETEtargetInterface: 'Eth47',
      sourceInterface: { labelText: 'Gi0/4', visible: true },
      targetInterface: { labelText: 'Eth47', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'router3',
      target: 'pod1_borderLeaf1',
      DELETEsourceInterface: 'Gi0/3',
      DELETEtargetInterface: 'Eth48',
      sourceInterface: { labelText: 'Gi0/3', visible: true },
      targetInterface: { labelText: 'Eth48', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'router3',
      target: 'pod1_borderLeaf2',
      DELETEsourceInterface: 'Gi0/4',
      DELETEtargetInterface: 'Eth46',  // Changed from Eth47 to avoid duplicate
      sourceInterface: { labelText: 'Gi0/4', visible: true },
      targetInterface: { labelText: 'Eth46', visible: true }
    });

    // Border Leaf to Spine Connections
    // Creating redundant paths through the spine layer
    await topoInstance.topology.addEdge({
      source: 'pod1_borderLeaf1',
      target: 'pod1_spine1',
      DELETEsourceInterface: 'Eth1',  // Changed from Eth0 to avoid duplicate
      DELETEtargetInterface: 'eth0',
      sourceInterface: { labelText: 'Eth1', visible: true },
      targetInterface: { labelText: 'eth0', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_borderLeaf1',
      target: 'pod1_spine2',
      DELETEsourceInterface: 'eth4',
      DELETEtargetInterface: 'eth4',
      sourceInterface: { labelText: 'eth4', visible: true },
      targetInterface: { labelText: 'eth4', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_borderLeaf2',
      target: 'pod1_spine1',
      DELETEsourceInterface: 'eth0',
      DELETEtargetInterface: 'eth7',
      sourceInterface: { labelText: 'eth0', visible: true },
      targetInterface: { labelText: 'eth7', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_borderLeaf2',
      target: 'pod1_spine2',
      DELETEsourceInterface: 'eth1',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth1', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    // Spine to Leaf Connections - Spine 1
    // Connecting first spine switch to all leaf switches
    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf1',
      DELETEsourceInterface: 'eth1',  // Changed from eth0 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth1', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf2',
      DELETEsourceInterface: 'eth2',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth2', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf3',
      DELETEsourceInterface: 'eth3',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth3', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf4',
      DELETEsourceInterface: 'eth4',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth4', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf5',
      DELETEsourceInterface: 'eth5',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth5', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf6',
      DELETEsourceInterface: 'eth6',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth6', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    // Spine to Leaf Connections - Spine 2
    // Connecting second spine switch to all leaf switches
    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf1',
      DELETEsourceInterface: 'eth0',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth0', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf2',
      DELETEsourceInterface: 'eth6',  // Changed from eth1 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth6', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf3',
      DELETEsourceInterface: 'eth2',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth2', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf4',
      DELETEsourceInterface: 'eth3',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth3', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf5',
      DELETEsourceInterface: 'eth7',  // Changed from eth4 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth7', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf6',
      DELETEsourceInterface: 'eth5',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth5', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });            

  }

  const addPODTwo = async () => {
    if (podTwoIsAdded) return;
    setPodTwoIsAdded(true);
    // Super-Spine Switches (Layer 2)
    await topoInstance.topology.addNode({
      id: 'superSpine1',
      iconName: 'network.switch',
      label: 'Super-Spine 1',
      layer: 300,
      coord: { x: -137.66666666666669, y: -131.38255122273628 }
    });

    await topoInstance.topology.addNode({
      id: 'superSpine2',
      iconName: 'network.switch',
      label: 'Super-Spine 2',
      layer: 300,
      coord: { x: 137.66666666666669, y: -131.38255122273628 }
    });

    // POD 2 Spine Switches (Layer 3)
    // Core backbone of the network, providing redundant paths
    await topoInstance.topology.addNode({
      id: 'pod2_spine1',
      iconName: 'network.switch',
      label: 'POD 2 Spine 1',
      layer: 301,
      coord: { x: -137.66666666666669, y: 32.2058162590879 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_spine2',
      iconName: 'network.switch',
      label: 'POD 2 Spine 2',
      layer: 301,
      coord: { x: 137.66666666666669, y: 32.2058162590879 }
    });

    // POD2 Leaf Switches (Layer 4)
    // Access layer switches that connect to end devices
    await topoInstance.topology.addNode({
      id: 'pod2_leaf1',
      iconName: 'network.switch',
      label: 'POD2 Leaf 1',
      layer: 302,
      coord: { x: -285, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf2',
      iconName: 'network.switch',
      label: 'POD2 Leaf 2',
      layer: 302,
      coord: { x: -171, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf3',
      iconName: 'network.switch',
      label: 'POD2 Leaf 3',
      layer: 302,
      coord: { x: -57, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf4',
      iconName: 'network.switch',
      label: 'POD2 Leaf 4',
      layer: 302,
      coord: { x: 57, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf5',
      iconName: 'network.switch',
      label: 'POD2 Leaf 5',
      layer: 302,
      coord: { x: 171, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf6',
      iconName: 'network.switch',
      label: 'POD2 Leaf 6',
      layer: 302,
      coord: { x: 285, y: 195.79418374091208 }
    });

    // Super-Spine to POD 2 Spine Connections
    // Creating redundant paths through the pod2_spine layer
    await topoInstance.topology.addEdge({
      source: 'superSpine1',
      target: 'pod2_spine1',
      DELETEsourceInterface: 'Eth1', 
      DELETEtargetInterface: 'eth0',
      sourceInterface: { labelText: 'Eth1', visible: true },
      targetInterface: { labelText: 'eth0', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine1',
      target: 'pod2_spine2',
      DELETEsourceInterface: 'eth4',
      DELETEtargetInterface: 'eth4',
      sourceInterface: { labelText: 'eth4', visible: true },
      targetInterface: { labelText: 'eth4', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine2',
      target: 'pod2_spine1',
      DELETEsourceInterface: 'eth0',
      DELETEtargetInterface: 'eth7',
      sourceInterface: { labelText: 'eth0', visible: true },
      targetInterface: { labelText: 'eth7', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine2',
      target: 'pod2_spine2',
      DELETEsourceInterface: 'eth1',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth1', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    // Super-Spine to POD 1 Spine Connections
    // Creating redundant paths through the pod1_spine layer
    await topoInstance.topology.addEdge({
      source: 'superSpine1',
      target: 'pod1_spine1',
      DELETEsourceInterface: 'eth22', 
      DELETEtargetInterface: 'eth28',
      sourceInterface: { labelText: 'eth22', visible: true },
      targetInterface: { labelText: 'eth28', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine1',
      target: 'pod1_spine2',
      DELETEsourceInterface: 'eth22',
      DELETEtargetInterface: 'eth28',
      sourceInterface: { labelText: 'eth22', visible: true },
      targetInterface: { labelText: 'eth28', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine2',
      target: 'pod1_spine1',
      DELETEsourceInterface: 'eth22',
      DELETEtargetInterface: 'eth27',
      sourceInterface: { labelText: 'eth22', visible: true },
      targetInterface: { labelText: 'eth27', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine2',
      target: 'pod1_spine2',
      DELETEsourceInterface: 'eth22',
      DELETEtargetInterface: 'eth27',
      sourceInterface: { labelText: 'eth22', visible: true },
      targetInterface: { labelText: 'eth27', visible: true }
    });

    // POD 2 Spine to POD2 Leaf Connections - POD 2 Spine 1
    // Connecting first pod2_spine switch to all pod2_leaf switches
    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf1',
      DELETEsourceInterface: 'eth1',  // Changed from eth0 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth1', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf2',
      DELETEsourceInterface: 'eth2',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth2', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf3',
      DELETEsourceInterface: 'eth3',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth3', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf4',
      DELETEsourceInterface: 'eth4',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth4', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf5',
      DELETEsourceInterface: 'eth5',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth5', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf6',
      DELETEsourceInterface: 'eth6',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth6', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    // POD 2 Spine to POD2 Leaf Connections - POD 2 Spine 2
    // Connecting second pod2_spine switch to all pod2_leaf switches
    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf1',
      DELETEsourceInterface: 'eth0',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth0', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf2',
      DELETEsourceInterface: 'eth6',  // Changed from eth1 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth6', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf3',
      DELETEsourceInterface: 'eth2',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth2', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf4',
      DELETEsourceInterface: 'eth3',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth3', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf5',
      DELETEsourceInterface: 'eth7',  // Changed from eth4 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth7', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf6',
      DELETEsourceInterface: 'eth5',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth5', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });
  }

  const addPodGroups = async () => {
    if (!topoInstance.topology.getGroup('pod1')) {
      // Create POD1 group - includes border leafs, spines, and leafs
      topoInstance.topology.addGroup({
        id: 'pod1',
        name: 'POD 1',
        memberNodes: [
          //'pod1_borderLeaf1', 'pod1_borderLeaf2',
          'pod1_spine1', 'pod1_spine2',
          'pod1_leaf1', 'pod1_leaf2', 'pod1_leaf3', 
          'pod1_leaf4', 'pod1_leaf5', 'pod1_leaf6'
        ]
      });
    }
    if (!topoInstance.topology.getGroup('pod2')) {
      // Create POD2 group - includes spines and leafs
      topoInstance.topology.addGroup({
        id: 'pod2',
        name: 'POD 2',
        memberNodes: [
          'pod2_spine1', 'pod2_spine2',
          'pod2_leaf1', 'pod2_leaf2', 'pod2_leaf3', 
          'pod2_leaf4', 'pod2_leaf5', 'pod2_leaf6'
        ]
      });
    }
  }

  const addInterPodPath = () => {
    if (topoInstance.topology.getPath('interPodPath')) return;
    topoInstance.topology.addPath({
      id: 'interPodPath',
      nodeIds: ['pod1_leaf2', 'pod1_spine1', 'superSpine2', 'pod2_spine2', 'pod2_leaf5'],
      animated: true,
      theme: {
        flowAnimationIcon: 'network.packet',
        strokeStyle: 'rgba(0, 0, 255, 0.7)',
      }
    });
  }




// Navigation and state management functions
  const handleScenarioChange = async (scenarioId) => {
    if (!topoInstance) return;
    const { topology } = topoInstance;
    
    try {
      // Reset all animated paths
      await topology.removePath('path1');
      await topology.removePath('interPodPath');
      
      // Reset node statuses
      topology.getNode('pod2_leaf5')?.setStatus('normal');
      topology.getNode('router1')?.setStatus('normal');
      
      // Remove dynamic nodes
      topology.removeNode('wan');
      setWanIsAdded(false);

      // Reset layout
      topology.setLayout('layered', { adjustZoom: false });
      
      // Reset groups
      topology.groups.forEach(g => topology.removeGroup(g.id));
      topology.addGroup({
        id: 'routers',
        name: 'Routers',
        memberNodes: ['router1', 'router2', 'router3']
      });

      // Reset router states
      ['router1', 'router2', 'router3'].forEach(nodeId => {
        const node = topology.getNode(nodeId);
        if (node) {
          node.setStatus('normal');
          Object.values(node.interfaces).forEach(iface => {
            if (iface.edge) {
              iface.edge.setStatus('normal');
              iface.setStatus('normal');
              iface.collapse();
            }
          });
        }
      });
    } catch (error) {
      console.error('Error executing scenario reset:', error);
    }
  };

  // Handle sub-step transitions
  const handleSubStepChange = async (subStepIndex) => {
    setCurrentSubStepIndex(subStepIndex);
    const scenario = scenarios[currentScenarioIndex];
    const subStep = scenario.subSteps[subStepIndex];
    if (subStep.action) {
      await subStep.action();
    }
  };

  // Handle next button logic
  const handleNext = async () => {
    const scenario = scenarios[currentScenarioIndex];
    const isLastSub = currentSubStepIndex >= scenario.subSteps.length - 1;
    
    if (!isLastSub) {
      // Move to next sub-step within the same scenario
      handleSubStepChange(currentSubStepIndex + 1);
    } else {
      // Check if we're at the last scenario
      const isLastScenario = currentScenarioIndex >= scenarios.length - 1;
      
      if (!isLastScenario) {
        // Move to the first sub-step of the next scenario
        const newIndex = currentScenarioIndex + 1;
        setCurrentScenarioIndex(newIndex);
        setCurrentSubStepIndex(0);
        await handleScenarioChange(scenarios[newIndex].id);
        
        // Execute first sub-step action
        const firstSub = scenarios[newIndex].subSteps[0];
        if (firstSub.action) await firstSub.action();
      } else {
        // Complete the demo
        setIsCompleted(true);
      }
    }
    
    // Reset view position
    topoInstance.topology.resetPanOffset();
  };

  // Handle back button logic
  const handleBack = async () => {
    if (isCompleted) {
      // Restart from beginning if we're on the completion screen
      setIsCompleted(false);
      setCurrentScenarioIndex(0);
      setCurrentSubStepIndex(0);
      await handleScenarioChange(scenarios[0].id);
      return;
    }

    if (currentSubStepIndex > 0) {
      // Move back one sub-step
      handleSubStepChange(currentSubStepIndex - 1);
    } else if (currentScenarioIndex > 0) {
      // Move to last sub-step of previous scenario
      const prevIndex = currentScenarioIndex - 1;
      setCurrentScenarioIndex(prevIndex);
      const lastSubOfPrev = scenarios[prevIndex].subSteps.length - 1;
      setCurrentSubStepIndex(lastSubOfPrev);
      await handleScenarioChange(scenarios[prevIndex].id);
    }
    
    // Reset view position
    topoInstance.topology.resetPanOffset();
  };

  // Initialize demo
  const handleStart = async () => {
    setIsDemoStarted(true);
    onDemoStart?.(); // Notify parent to hide header
    
    // Initialize first scenario
    await handleScenarioChange(scenarios[0].id);
    
    // Execute first sub-step action
    const first = scenarios[0].subSteps[0];
    if (first.action) {
      await first.action();
    }

    // Set up layout handlers for group operations
    window.addEventListener('topoSphere.groupExpanded', () => {
      setTimeout(() => topoInstance.topology.setLayout('layered'), 200);
    });
    window.addEventListener('topoSphere.groupCollapsed', () => {
      setTimeout(() => topoInstance.topology.setLayout('layered'), 200);
    });
  };

  // Render welcome screen
  if (!isDemoStarted) {
    return (
      <div className="pointer-events-auto bg-[#0a1425] text-white h-full flex flex-col min-h-0">
        <div className="flex-1 px-4 py-3 flex flex-col">
          <div className="flex-1 min-h-0 flex flex-col justify-center">
            <h3 className="text-lg font-medium text-white/90 mb-2">
              Seamless network mapping, anywhere
            </h3>
            <p className="text-sm text-white/70 leading-normal">
              topoSphere delivers smooth network visualization on any screen size. With native touch support, explore networks on the go—or use a larger screen for the best experience.
            </p>
          </div>
          <button
            onClick={handleStart}
            className="w-full mt-3 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg
              transition-colors duration-200 flex items-center justify-center gap-2 
              font-medium text-sm"
          >
            Start Interactive Demo
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Render completion screen
  if (isCompleted) {
    return (
      <div className="pointer-events-auto bg-[#0a1425] text-white h-full flex flex-col min-h-0">
        <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
          <div className="w-12 h-12 mb-3 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-green-500">
              <path 
                d="M20 6L9 17L4 12" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h4 className="text-lg font-medium mb-3">
            Enjoyed the demo? Share your feedback and be the first to know!
          </h4>
          <div className="w-full space-y-3 max-w-xs">
            <a
              href="#waitlist"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#waitlist');
              }}
              className="block w-full bg-gradient-to-r from-[#d35400] to-[#e67e22] 
                text-white px-4 py-2.5 rounded-lg text-sm font-semibold 
                hover:from-[#b24300] hover:to-[#c57c1f] transition-all shadow-lg
                flex items-center justify-center gap-2"
            >
              <Rocket className="w-4 h-4" />
              Join the Waitlist
            </a>
            <button
              onClick={handleBack}
              className="w-full px-4 py-2 border border-white/20 text-white/90 rounded-lg 
                hover:bg-white/10 transition-colors text-sm flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Review Demo Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get current scenario and sub-step
  const scenario = scenarios[currentScenarioIndex];
  const subStep = scenario.subSteps[currentSubStepIndex];
  
  // Render main tutorial interface
  return (
    <div className="pointer-events-none bg-[#0a1425] text-white h-full flex flex-col min-h-0">
      <div className="pointer-events-auto flex flex-col h-full min-h-0">
        {/* Optimized header with improved text handling */}
        <div className="border-b border-white/10 px-2 py-2">
          <div className="flex items-center gap-1 text-sm">
            {/* Step counter - made more compact */}
            <span className="bg-white/10 rounded px-1 py-0.5 text-xs text-white/70 shrink-0">
              {currentScenarioIndex + 1}.{currentSubStepIndex + 1}
            </span>
            
            {/* Main section - allows more space for text */}
            <span className="bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
              {scenario.label}
            </span>
            
            {/* Arrow separator - minimal width */}
            <span className="text-white/40 mx-0.5 shrink-0">→</span>
            
            {/* Sub-step - allows text to shrink before truncating */}
            <span className="text-white/80 whitespace-nowrap overflow-hidden text-ellipsis min-w-0 flex-1">
              {subStep.label}
            </span>
          </div>
        </div>

        {/* Content area with instructions */}
        <div className="p-3 h-32 overflow-y-auto">
        <p className="text-sm text-white/70 leading-normal pr-2">
            {subStep.detail}
          </p>
        </div>

        {/* Navigation controls */}
        <div className="border-t border-white/10 p-2 flex gap-2">
          <button
            onClick={handleBack}
            disabled={isBackDisabled || (currentScenarioIndex === 0 && currentSubStepIndex === 0)}
            className="flex-1 py-2 px-4 rounded-lg text-sm font-medium
              flex items-center justify-center
              disabled:opacity-50 disabled:cursor-not-allowed
              border border-white/20 text-white/90"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          
          <button
            onClick={handleNext}
            disabled={isNextDisabled}
            className="flex-1 py-2 px-4 rounded-lg text-sm font-medium
              flex items-center justify-center
              disabled:opacity-50 disabled:cursor-not-allowed
              bg-blue-500 hover:bg-blue-600 text-white"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileGuidedDemo;
